/**
 * MediaPreview Component
 *
 * Displays media (images, videos) in chat messages with lightbox support.
 *
 * Usage:
 *   <MediaPreview media={[
 *     { type: 'image', url: '...', caption: '...' }
 *   ]} />
 */

import React, { useState } from 'react';
import { theme } from '@/styles/theme';

export interface MediaItem {
  type: 'image' | 'video' | 'audio';
  url: string;
  caption?: string;
  thumbnail_url?: string;
}

export interface MediaPreviewProps {
  media: MediaItem[];
}

export const MediaPreview: React.FC<MediaPreviewProps> = ({ media }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  if (!media || media.length === 0) return null;

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % media.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + media.length) % media.length);
  };

  // Handle keyboard navigation in lightbox
  React.useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  // Grid layout based on number of items
  const getGridClass = () => {
    if (media.length === 1) return 'grid-cols-1';
    if (media.length === 2) return 'grid-cols-2';
    if (media.length === 3) return 'grid-cols-3';
    return 'grid-cols-2';
  };

  return (
    <>
      {/* Media Grid */}
      <div className={`grid ${getGridClass()} gap-2 max-w-md`}>
        {media.map((item, index) => (
          <div
            key={index}
            className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-card-darkAlt cursor-pointer hover:opacity-90 transition-opacity h-32 w-32"
            onClick={() => item.type === 'image' && openLightbox(index)}
          >
            {item.type === 'image' && (
              <img
                src={item.url}
                alt={item.caption || `Image ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}

            {item.type === 'video' && (
              <video
                src={item.url}
                poster={item.thumbnail_url}
                controls
                className="w-full h-full"
                preload="metadata"
              >
                Your browser does not support the video tag.
              </video>
            )}

            {item.type === 'audio' && (
              <div className="p-4 flex items-center justify-center">
                <audio src={item.url} controls className="w-full">
                  Your browser does not support the audio tag.
                </audio>
              </div>
            )}

            {/* Caption Overlay */}
            {item.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-3 py-2">
                {item.caption}
              </div>
            )}

            {/* Play Icon for Videos */}
            {item.type === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-16 h-16 bg-black/50 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && media[lightboxIndex]?.type === 'image' && (
        <div
          className="fixed inset-0 z-[1060] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Close lightbox"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Previous Button */}
          {media.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="absolute left-4 p-3 text-white hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Previous image"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Image */}
          <div className="max-w-7xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={media[lightboxIndex].url}
              alt={media[lightboxIndex].caption || `Image ${lightboxIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />

            {/* Caption */}
            {media[lightboxIndex].caption && (
              <p className="text-white text-center mt-4 text-sm">
                {media[lightboxIndex].caption}
              </p>
            )}

            {/* Counter */}
            {media.length > 1 && (
              <p className="text-white text-center mt-2 text-xs opacity-75">
                {lightboxIndex + 1} / {media.length}
              </p>
            )}
          </div>

          {/* Next Button */}
          {media.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-4 p-3 text-white hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Next image"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default MediaPreview;
